import React, { useState, useEffect } from 'react';
import {
  Fade,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Button,
  Stack,
} from '@mui/material';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';
import TextField from '@mui/material/TextField';
import BorderedSection from '../../shared/borderedSection';
import { useFormik } from 'formik';
import { FormValues } from '../types/propertyDetailFormValues';
import defaultBuildingImage from '../../../assets/images/defaultBuilding.jpg';
import { usePropertyOrganizationOptionsQuery } from '../../../../types/generated-types';
import { PropertyImageUploadDialog } from './property-image-upload-dialog';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import SaveIcon from '@mui/icons-material/Save';

export function PropertyGeneralTab({
  formik,
  onChange,
  onSaveImageUpdate,
}: {
  formik: ReturnType<typeof useFormik<FormValues>>;
  onChange: StandardInputProps['onChange'];
  onSaveImageUpdate?: () => void;
}) {
  const { data: organizationData } = usePropertyOrganizationOptionsQuery();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, value);
  };

  const handleImageUploaded = (imageUrl: string) => {
    formik.setFieldValue('imageUrl', imageUrl);
    setImageError(false); // Reset error state when new image is uploaded
    setUploadDialogOpen(false);
  };

  // Reset image error state when imageUrl changes
  useEffect(() => {
    setImageError(false);
  }, [formik.values.imageUrl]);

  // Extract a display name from the Cloudinary URL
  const getImageDisplayName = (url: string | undefined): string => {
    if (!url) return '';

    try {
      // Cloudinary URLs typically look like:
      // https://res.cloudinary.com/cloud_name/image/upload/v1234567890/folder/filename.jpg
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      const filename = pathParts[pathParts.length - 1];

      // If we have a filename, return it; otherwise return a generic name
      if (filename && filename.includes('.')) {
        return filename;
      }

      // Extract from public_id if available (usually in the path)
      const uploadIndex = pathParts.indexOf('upload');
      if (uploadIndex !== -1 && pathParts.length > uploadIndex + 2) {
        // Skip version number, get the rest
        const publicIdParts = pathParts.slice(uploadIndex + 2);
        const lastPart = publicIdParts[publicIdParts.length - 1];
        return lastPart || 'Property Image';
      }

      return 'Property Image';
    } catch {
      // If URL parsing fails, try to extract filename from the end
      const lastSlash = url.lastIndexOf('/');
      if (lastSlash !== -1) {
        const filename = url.substring(lastSlash + 1);
        // Remove query parameters if any
        const filenameWithoutParams = filename.split('?')[0];
        return filenameWithoutParams || 'Property Image';
      }
      return 'Property Image';
    }
  };

  return (
    <Fade in={true} style={{ transitionDuration: '1100ms' }}>
      <div style={{ width: '100%' }}>
        <Grid container columnSpacing={2} rowSpacing={{ xs: 3 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              id="propTitle"
              name="propTitle"
              label="Title"
              value={formik.values.propTitle}
              onChange={onChange}
              error={
                formik.touched.propTitle && Boolean(formik.errors.propTitle)
              }
              helperText={formik.touched.propTitle && formik.errors.propTitle}
            />
            <br />
            <br />
            <FormControl fullWidth>
              <InputLabel id="organization-label">
                Owning Organization
              </InputLabel>
              <Select
                labelId="organization-label"
                id="organizationId"
                name="organizationId"
                value={formik.values.organizationId || ''}
                onChange={handleSelectChange}
                label="Owning Organization"
              >
                <MenuItem value="">
                  <em>Select an organization (optional)</em>
                </MenuItem>
                {organizationData?.organizations?.map((org) => (
                  <MenuItem key={org._id} value={org._id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <br />
            <br />
            <TextField
              fullWidth
              required
              id="line1"
              name="line1"
              label="Address"
              value={formik.values.line1}
              onChange={onChange}
              error={formik.touched.line1 && Boolean(formik.errors.line1)}
              helperText={formik.touched.line1 && formik.errors.line1}
            />
            <br />
            <br />
            <TextField
              fullWidth
              id="line2"
              name="line2"
              label="Address 2"
              value={formik.values.line2}
              onChange={onChange}
              error={formik.touched.line2 && Boolean(formik.errors.line2)}
              helperText={formik.touched.line2 && formik.errors.line2}
            />
            <br />
            <br />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  required
                  id="city"
                  name="city"
                  label="City"
                  value={formik.values.city}
                  onChange={onChange}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                />
              </Grid>
              <br />
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  required
                  id="state"
                  name="state"
                  label="State"
                  value={formik.values.state}
                  onChange={onChange}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                />
              </Grid>
              <br />
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  required
                  id="postalCode"
                  name="postalCode"
                  label="Postal Code"
                  value={formik.values.postalCode}
                  onChange={onChange}
                  error={
                    formik.touched.postalCode &&
                    Boolean(formik.errors.postalCode)
                  }
                  helperText={
                    formik.touched.postalCode && formik.errors.postalCode
                  }
                />
              </Grid>
              <br />
            </Grid>
            <br />
            <BorderedSection title="Property Image">
              <Grid container spacing={2} sx={{ marginLeft: '0' }}>
                <Grid item xs={12} md={6} lg={6}>
                  <TextField
                    fullWidth
                    id="imageUrl"
                    name="imageUrl"
                    label="Image Name:"
                    variant="standard"
                    value={
                      getImageDisplayName(formik.values.imageUrl) ||
                      'No image uploaded'
                    }
                    InputProps={{
                      readOnly: true,
                      sx: {
                        color: 'primary.main',
                        width: '85%',
                      },
                    }}
                    error={
                      formik.touched.imageUrl && Boolean(formik.errors.imageUrl)
                    }
                    helperText={
                      formik.touched.imageUrl && formik.errors.imageUrl
                        ? formik.errors.imageUrl
                        : formik.values.imageUrl !==
                            formik.initialValues.imageUrl
                          ? 'Image updated. Click "Save update" to save your changes.'
                          : formik.values.imageUrl
                            ? 'Click "Edit Photo" to change the image of your property'
                            : 'Click "Add Photo" to upload an image of your property'
                    }
                  />
                  <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 2 }}>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<AddPhotoAlternateIcon />}
                      onClick={() => setUploadDialogOpen(true)}
                      sx={{ color: 'white' }}
                    >
                      {formik.values.imageUrl ? 'Edit Photo' : 'Add Photo'}
                    </Button>
                    {formik.values.imageUrl !==
                      formik.initialValues.imageUrl && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<SaveIcon />}
                        onClick={() =>
                          onSaveImageUpdate?.() ?? formik.submitForm()
                        }
                        disabled={!formik.isValid || formik.isSubmitting}
                        sx={{ color: 'white' }}
                      >
                        {formik.isSubmitting ? 'Saving...' : 'Save update'}
                      </Button>
                    )}
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={6}
                  sx={{
                    paddingTop: '0px!important',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    alt="Preview"
                    src={
                      imageError || !formik.values.imageUrl
                        ? defaultBuildingImage
                        : formik.values.imageUrl
                    }
                    width="90%"
                    style={{
                      objectFit: 'cover',
                      height: '225px',
                      marginTop: 'auto',
                      width: 'auto',
                    }}
                    loading="lazy"
                    onError={() => setImageError(true)}
                    onLoad={() => {
                      // Reset error state when a new image successfully loads
                      if (imageError && formik.values.imageUrl) {
                        setImageError(false);
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </BorderedSection>
            <PropertyImageUploadDialog
              open={uploadDialogOpen}
              onClose={() => setUploadDialogOpen(false)}
              onImageUploaded={handleImageUploaded}
              currentImageUrl={formik.values.imageUrl}
            />
            <br />
          </Grid>
          <Grid item xs={12} md={6}>
            <BorderedSection title="Manager">
              <TextField
                fullWidth
                id="propManagerName"
                name="propManagerName"
                variant="standard"
                label="Name"
                value={formik.values.propManagerName}
                onChange={onChange}
                error={
                  formik.touched.propManagerName &&
                  Boolean(formik.errors.propManagerName)
                }
                helperText={
                  formik.touched.propManagerName &&
                  formik.errors.propManagerName
                }
              />
              <br />
              <TextField
                fullWidth
                id="propManagerEmail"
                name="propManagerEmail"
                variant="standard"
                label="Email"
                value={formik.values.propManagerEmail}
                onChange={onChange}
                error={
                  formik.touched.propManagerEmail &&
                  Boolean(formik.errors.propManagerEmail)
                }
                helperText={
                  formik.touched.propManagerEmail &&
                  formik.errors.propManagerEmail
                }
              />
              <br />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id="propManagerPhone"
                    name="propManagerPhone"
                    variant="standard"
                    label="Phone"
                    placeholder="(123) 456-7890"
                    value={formik.values.propManagerPhone}
                    onChange={onChange}
                    error={
                      formik.touched.propManagerPhone &&
                      Boolean(formik.errors.propManagerPhone)
                    }
                    helperText={
                      formik.touched.propManagerPhone &&
                      formik.errors.propManagerPhone
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id="propManagerMobile"
                    name="propManagerMobile"
                    variant="standard"
                    label="Mobile"
                    placeholder="(123) 456-7890"
                    value={formik.values.propManagerMobile}
                    onChange={onChange}
                    error={
                      formik.touched.propManagerMobile &&
                      Boolean(formik.errors.propManagerMobile)
                    }
                    helperText={
                      formik.touched.propManagerMobile &&
                      formik.errors.propManagerMobile
                    }
                  />
                </Grid>
              </Grid>
            </BorderedSection>
          </Grid>
          <Grid item xs={12} md={6}>
            <BorderedSection title="Maintance Manager">
              <TextField
                fullWidth
                id="propMaintManagerName"
                name="propMaintManagerName"
                variant="standard"
                label="Name"
                value={formik.values.propMaintManagerName}
                onChange={onChange}
                error={
                  formik.touched.propMaintManagerName &&
                  Boolean(formik.errors.propMaintManagerName)
                }
                helperText={
                  formik.touched.propMaintManagerName &&
                  formik.errors.propMaintManagerName
                }
              />
              <br />
              <TextField
                fullWidth
                id="propMaintManagerEmail"
                name="propMaintManagerEmail"
                variant="standard"
                label="Email"
                value={formik.values.propMaintManagerEmail}
                onChange={onChange}
                error={
                  formik.touched.propMaintManagerEmail &&
                  Boolean(formik.errors.propMaintManagerEmail)
                }
                helperText={
                  formik.touched.propMaintManagerEmail &&
                  formik.errors.propMaintManagerEmail
                }
              />
              <br />
              <TextField
                fullWidth
                id="propMaintManagerMobile"
                name="propMaintManagerMobile"
                variant="standard"
                label="Mobile"
                placeholder="(123) 456-7890"
                value={formik.values.propMaintManagerMobile}
                onChange={onChange}
                error={
                  formik.touched.propMaintManagerMobile &&
                  Boolean(formik.errors.propMaintManagerMobile)
                }
                helperText={
                  formik.touched.propMaintManagerMobile &&
                  formik.errors.propMaintManagerMobile
                }
              />
            </BorderedSection>
          </Grid>
          <Grid item xs={12} md={6}>
            <BorderedSection title="Owner">
              <TextField
                fullWidth
                id="propOwnerName"
                name="propOwnerName"
                variant="standard"
                label="Name"
                value={formik.values.propOwnerName}
                onChange={onChange}
                error={
                  formik.touched.propOwnerName &&
                  Boolean(formik.errors.propOwnerName)
                }
                helperText={
                  formik.touched.propOwnerName && formik.errors.propOwnerName
                }
              />
              <br />
              <TextField
                fullWidth
                id="propOwnerEmail"
                name="propOwnerEmail"
                variant="standard"
                label="Email"
                value={formik.values.propOwnerEmail}
                onChange={onChange}
                error={
                  formik.touched.propOwnerEmail &&
                  Boolean(formik.errors.propOwnerEmail)
                }
                helperText={
                  formik.touched.propOwnerEmail && formik.errors.propOwnerEmail
                }
              />
              <br />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id="propOwnerPhone"
                    name="propOwnerPhone"
                    variant="standard"
                    label="Phone"
                    placeholder="(123) 456-7890"
                    value={formik.values.propOwnerPhone}
                    onChange={onChange}
                    error={
                      formik.touched.propOwnerPhone &&
                      Boolean(formik.errors.propOwnerPhone)
                    }
                    helperText={
                      formik.touched.propOwnerPhone &&
                      formik.errors.propOwnerPhone
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    id="propOwnerMobile"
                    name="propOwnerMobile"
                    variant="standard"
                    label="Mobile"
                    placeholder="(123) 456-7890"
                    value={formik.values.propOwnerMobile}
                    onChange={onChange}
                    error={
                      formik.touched.propOwnerMobile &&
                      Boolean(formik.errors.propOwnerMobile)
                    }
                    helperText={
                      formik.touched.propOwnerMobile &&
                      formik.errors.propOwnerMobile
                    }
                  />
                </Grid>
              </Grid>
            </BorderedSection>
          </Grid>
        </Grid>
      </div>
    </Fade>
  );
}
